import React from "react";
import { FormattedDate, useIntl } from "react-intl";
import { Container, Grid, Typography, Box, Chip } from "@material-ui/core";
import { useSelector } from "react-redux";
import { useStyles } from "./detailStyles";
import DetailActions from "./DetailActions";
import DetailDivider from "./DetailDivider";
import DetailRow from "./DetailRow";
import translate from "../../../i18n/translate";
import PaymentCardsLogos from "../PaymentCardsLogos";
import { SrDialog, SrCurrency, SrButton } from "../../common";
import { getTransactionTypeIntId, CARD } from "../constants";

const IVA = 0.16;
const getTotalAfterTaxes = (amount, commission) =>
  parseFloat(amount) > 0 ? Number(amount) - Number(commission) : amount;

const sustractIVA = (amount, fee) => {
  return amount / (1 + IVA);
};

const getFormattedFee = (fee) => {
  return fee.percentage ? (
    `${fee.percentage}%`
  ) : (
    <SrCurrency value={sustractIVA(fee.amount)} />
  );
};

const Detail = ({
  isOpen,
  onClose,
  transaction,
  updateTransactions,
  onRelatedClick,
}) => {
  const classes = useStyles();
  const { detail, key, paymentMethod, cardHolderName } = transaction;
  const totalAfterTaxes = getTotalAfterTaxes(
    transaction.total,
    transaction.detail.commission
  );
  const { user } = useSelector(({ auth }) => auth);
  const intl = useIntl();
  const isNegativeValue = transaction.total < 0;
  const sourceEmail = transaction.detail.transference?.source.email;

  return (
    <SrDialog
      heading={
        <>
          {`#${detail.authorization_code}`}
          {transaction.type === "D" && (
            <Chip
              className={classes.returnChip}
              label={intl.formatMessage({
                id: "resume.transactions.return",
              })}
            />
          )}
          {transaction.hasDevolution && (
            <Chip
              className={classes.returnedChip}
              label={intl.formatMessage({
                id: "resume.transactions.cancelled",
              })}
            />
          )}
        </>
      }
      size="md"
      open={isOpen}
      handleClose={onClose}
    >
      <Grid>
        <Typography className={classes.type}>
          {translate(getTransactionTypeIntId(transaction?.sellChannel))}
        </Typography>
      </Grid>
      <Grid item>
        <Grid container justifyContent="flex-start" spacing={2}>
          <Grid item md={9}>
            <Container
              component="section"
              aria-label={intl.formatMessage({
                id: "transactions.details",
              })}
              className={classes.detailCard}
            >
              <Grid container direction="row" spacing={2}>
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="flex-end"
                  spacing={2}
                >
                  <Grid item>
                    <Typography className={classes.amountText}>
                      {translate("resume.transactionDetails.amount")}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography className={classes.status}>
                      {translate("resume.transactionDetails.status.paid")}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container justifyContent="space-between" spacing={2}>
                  <Grid item>
                    <Typography
                      className={`${classes.amount} ${
                        transaction.total >= 0 || transaction.hasDevolution
                          ? classes.positiveValue
                          : classes.negativeValue
                      }`}
                    >
                      <SrCurrency
                        value={
                          transaction.hasDevolution
                            ? -transaction.total
                            : transaction.total
                        }
                        currency={transaction.currency}
                      />
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container justifyContent="space-between" spacing={2}>
                  <Grid item>
                    <Typography className={classes.description}>
                      <FormattedDate
                        value={detail.date}
                        year="numeric"
                        month="long"
                        day="2-digit"
                        hour="numeric"
                        minute="numeric"
                      />
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography className={classes.description}>
                      {translate("resume.transactionDetails.authorization")}{" "}
                      {detail.authorization_code}
                    </Typography>
                  </Grid>
                </Grid>

                <DetailDivider />
                <DetailRow
                  label={translate("resume.transactionDetails.tip")}
                  value={
                    <SrCurrency
                      value={detail.tip}
                      currency={transaction.currency}
                    />
                  }
                />
                {transaction.detail.fees &&
                  transaction.detail.fees.map((fee) => (
                    <DetailRow
                      key={fee.amount || fee.percentage || fee.description}
                      label={
                        <>
                          <span className={classes.commissionDescription}>
                            {fee.description
                              ? fee.description.toLowerCase()
                              : translate(
                                  "resume.transactionDetails.commission"
                                )}
                          </span>
                          &nbsp;
                          <span className={classes.commissionDetail}>
                            {fee.type === "fee"
                              ? translate(
                                  "resume.transactionDetails.commissionDetail",
                                  { fee: getFormattedFee(fee) }
                                )
                              : fee.percentage && `${fee.percentage} %`}
                          </span>
                        </>
                      }
                      value={
                        <SrCurrency
                          value={`-${fee.amount}`}
                          currency={transaction.currency}
                        />
                      }
                      positiveValue={false}
                    />
                  ))}

                {transaction.detail.transference ? (
                  <DetailRow
                    label={translate("resume.transactionDetails.totalAmount")}
                    value={
                      <SrCurrency
                        value={`${user.username === sourceEmail ? "-" : ""}${
                          transaction.detail.total
                        }`}
                        currency={transaction.currency}
                      />
                    }
                    positiveValue={user.username !== sourceEmail}
                  />
                ) : (
                  <DetailRow
                    label={translate("resume.transactionDetails.totalAmount")}
                    value={
                      <SrCurrency
                        value={
                          transaction.hasDevolution
                            ? -totalAfterTaxes
                            : totalAfterTaxes
                        }
                        currency={transaction.currency}
                      />
                    }
                    positiveValue={
                      totalAfterTaxes > 0 || transaction.hasDevolution
                    }
                  />
                )}
                <DetailDivider />

                <Grid container justifyContent="space-between" spacing={2}>
                  <Grid item>
                    <Typography className={classes.description}>
                      {cardHolderName}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Grid container alignItems="center">
                      {paymentMethod.cardID && (
                        <>
                          <PaymentCardsLogos imageKey={paymentMethod.cardID} />
                          <Box
                            fontWeight="fontWeightBold"
                            component="span"
                            marginLeft={1}
                          >
                            ••••
                            <span className={classes.cardNumber}>
                              {paymentMethod.cardNumber}
                            </span>
                          </Box>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Container>
            {transaction.related && (
              <Grid container>
                <Grid item>
                  <Box mt={3}>
                    <SrButton
                      onClick={() => onRelatedClick(transaction.related)}
                    >
                      {intl.formatMessage({
                        id: "transactions.related",
                      })}
                    </SrButton>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid item md={3} className={classes.actionPanel}>
            <DetailActions
              id={key}
              url={detail.url}
              showCancelButton={
                transaction?.sellChannel === CARD && !isNegativeValue
              }
              updateTransactions={updateTransactions}
              onClose={onClose}
            />
          </Grid>
        </Grid>
      </Grid>
    </SrDialog>
  );
};
export default Detail;
